export type PostIdea = {
  title: string;
  digest?: string;
  format_template?: string;
  length_tag?: string;
  tone_style?: any;
  keywords?: string[];
  section_layout?: string[];
  element_settings?: Record<string, any>;
  content_type?: string;
};
export type PostDraft = { title: string; intro: string; digest?: string };

// Strict: only accept raw JSON with schema { ideas: [{ title, digest? }] } or array
export function extractIdeasStrict(text: string): PostIdea[] {
  try {
    const parsed = JSON.parse(text);
    const ideas = Array.isArray(parsed) ? parsed : parsed?.ideas;
    if (Array.isArray(ideas)) {
      return ideas
        .map((p: any) => ({
          title: String(p.title || p.name || ''),
          digest: p.digest || p.summary,
          format_template: p.format_template || p.template,
          length_tag: p.length_tag || p.length,
          tone_style: p.tone_style || p.tone,
          keywords: Array.isArray(p.keywords) ? p.keywords : (typeof p.keywords === 'string' ? p.keywords.split(',').map((k: string) => k.trim()).filter(Boolean) : undefined),
          section_layout: p.section_layout,
          element_settings: p.element_settings,
          content_type: p.content_type
        }))
        .filter((p: PostIdea) => p.title.length > 0);
    }
  } catch {}
  // Minimal tolerance: if JSON is embedded in surrounding text, extract the first JSON object
  try {
    const start = text.indexOf('{');
    const end = text.lastIndexOf('}');
    if (start !== -1 && end > start) {
      const maybe = text.slice(start, end + 1);
      const parsed = JSON.parse(maybe);
      const ideas = Array.isArray(parsed) ? parsed : parsed?.ideas;
      if (Array.isArray(ideas)) {
        return ideas
          .map((p: any) => ({
            title: String(p.title || p.name || ''),
            digest: p.digest || p.summary,
            format_template: p.format_template || p.template,
            length_tag: p.length_tag || p.length,
            tone_style: p.tone_style || p.tone,
            keywords: Array.isArray(p.keywords) ? p.keywords : (typeof p.keywords === 'string' ? p.keywords.split(',').map((k: string) => k.trim()).filter(Boolean) : undefined),
            section_layout: p.section_layout,
            element_settings: p.element_settings,
            content_type: p.content_type
          }))
          .filter((p: PostIdea) => p.title.length > 0);
      }
    }
  } catch {}
  return [];
}

// Extract a single full post draft in strict JSON form.
// Accepted JSON shapes:
// { post: { title: string, intro: string, digest?: string } }
// { title: string, intro: string, digest?: string }
export function extractPostStrict(text: string): PostDraft | null {
  const tryParse = (t: string) => {
    try {
      return JSON.parse(t);
    } catch {
      return null;
    }
  };

  let parsed = tryParse(text);
  if (!parsed) {
    // Minimal tolerance: locate first JSON object in text
    try {
      const start = text.indexOf('{');
      const end = text.lastIndexOf('}');
      if (start !== -1 && end > start) parsed = JSON.parse(text.slice(start, end + 1));
    } catch {}
  }

  if (!parsed) return null;

  const candidate: any = parsed.post || parsed;
  if (candidate && typeof candidate.title === 'string') {
    const intro = typeof candidate.intro === 'string' ? candidate.intro : (typeof candidate.body === 'string' ? candidate.body : undefined);
    if (typeof intro === 'string') {
      return { title: candidate.title, intro, digest: candidate.digest } as PostDraft;
    }
  }
  return null;
}

// Loose extraction: derive a draft from plain text (title on first non-empty line, rest as body)
export function extractPostLoose(text: string): PostDraft | null {
  if (!text || typeof text !== 'string') return null;
  
  // Skip if text looks like conversational chat (starts with common chat phrases)
  const chatPhrases = [
    /^Det bedste at skrive om/i,
    /^Her er et par forslag/i,
    /^Hvilket emne lyder/i,
    /^What should you write about/i,
    /^Here are some suggestions/i,
    /^Which topic sounds/i,
    /^Based on your website/i,
    /^I can help you/i,
    /^Let me suggest/i
  ];
  
  if (chatPhrases.some(phrase => phrase.test(text))) {
    return null;
  }
  
  const lines = text.split(/\r?\n/).map(l => l.trim());
  const nonEmpty = lines.filter(l => l.length > 0);
  if (nonEmpty.length < 2) return null;

  // If the text starts with an instruction like 'Skriv et post om "Title":', try to capture quoted title
  const instrMatch = text.match(/Skriv\s+et\s+post\s+om\s+"([^"]+)"/i);
  if (instrMatch && instrMatch[1]) {
    const title = instrMatch[1].trim();
    const afterIdx = text.indexOf(instrMatch[0]);
    const rest = text.slice(afterIdx + instrMatch[0].length).trim();
    const intro = rest.replace(/^[:\-\s]+/, '').trim();
    if (title && intro && intro.length > 80) return { title, intro };
  }

  const title = nonEmpty[0];
  const intro = nonEmpty.slice(1).join('\n').trim();
  if (title && intro && intro.length > 80) return { title, intro };
  return null;
}


